import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Divider, FormControlLabel, Grid,
  IconButton, MenuItem, Select, Stack, Switch, Tab, Tabs, TextField, Typography
} from '@mui/material';
import {
  Construction, Globe2, Plus, Rocket, ShieldCheck, Trash2
} from 'lucide-react';
import { getSiteAvailability, updateSiteAvailability } from '../../features/siteControl/siteAvailabilityAPI';

const propertyFallback = [
  { id: 'clikkle-main', domain: 'clikkle.com', name: 'Clikkle' },
  { id: 'n5', domain: 'n5.clikkle.com', name: 'N5' },
  { id: 'core', domain: 'core.clikkle.com', name: 'G7 Core' },
  { id: 'worksuite', domain: 'worksuite.clikkle.com', name: 'Worksuite' },
  { id: 'aurora', domain: 'aurora.clikkle.com', name: 'Aurora' },
  { id: 'marketplace', domain: 'marketplace.clikkle.com', name: 'Marketplace' },
];

const modes = [
  { value: 'normal', label: 'Normal', detail: 'Serve the normal experience.', icon: Globe2 },
  { value: 'maintenance', label: 'Maintenance', detail: 'Show the approved maintenance experience.', icon: Construction },
  { value: 'coming-soon', label: 'Coming Soon', detail: 'Show the approved Coming Soon experience.', icon: Rocket },
];

const holdModes = modes.filter((mode) => mode.value !== 'normal');

const toLocalInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const blankScope = () => ({
  manualMode: 'normal',
  scheduleEnabled: false,
  scheduledMode: 'maintenance',
  startsAt: '',
  endsAt: '',
});

const normalizeScope = (scope = {}) => ({
  manualMode: scope.manualMode || 'normal',
  scheduleEnabled: Boolean(scope.scheduleEnabled),
  scheduledMode: scope.scheduledMode || 'maintenance',
  startsAt: toLocalInput(scope.startsAt),
  endsAt: toLocalInput(scope.endsAt),
});

const normalizeRoute = (rule = {}) => ({
  _id: rule._id,
  path: rule.path || '/',
  manualMode: rule.manualMode || 'maintenance',
  scheduleEnabled: Boolean(rule.scheduleEnabled),
  scheduledMode: rule.scheduledMode || 'maintenance',
  startsAt: toLocalInput(rule.startsAt),
  endsAt: toLocalInput(rule.endsAt),
});

const serializeScope = (scope) => ({
  ...scope,
  startsAt: scope.scheduleEnabled && scope.startsAt ? new Date(scope.startsAt).toISOString() : null,
  endsAt: scope.scheduleEnabled && scope.endsAt ? new Date(scope.endsAt).toISOString() : null,
});

function ModeCards({ value, onChange, includeNormal = true }) {
  const choices = includeNormal ? modes : holdModes;
  return (
    <Grid container spacing={1.5}>
      {choices.map(({ value: modeValue, label, detail, icon: ModeIcon }) => {
        const selected = value === modeValue;
        return (
          <Grid item xs={12} md={includeNormal ? 4 : 6} key={modeValue}>
            <Card
              onClick={() => onChange(modeValue)}
              sx={{
                cursor: 'pointer', height: '100%', border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? 'action.selected' : 'background.paper'
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.1} mb={0.5}>
                  <ModeIcon size={18}/><Typography fontWeight={700}>{label}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">{detail}</Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

function ScheduleEditor({ scope, onChange, label = 'Scheduled override' }) {
  return (
    <Stack spacing={1.5} mt={2}>
      <FormControlLabel
        control={<Switch checked={scope.scheduleEnabled} onChange={(e) => onChange({ ...scope, scheduleEnabled: e.target.checked })}/>} 
        label={label}
      />
      {scope.scheduleEnabled && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <Select fullWidth size="small" value={scope.scheduledMode} onChange={(e) => onChange({ ...scope, scheduledMode: e.target.value })}>
              <MenuItem value="maintenance">Maintenance</MenuItem>
              <MenuItem value="coming-soon">Coming Soon</MenuItem>
            </Select>
            <TextField fullWidth size="small" type="datetime-local" label="Start" InputLabelProps={{ shrink: true }} value={scope.startsAt} onChange={(e) => onChange({ ...scope, startsAt: e.target.value })}/>
            <TextField fullWidth size="small" type="datetime-local" label="End" InputLabelProps={{ shrink: true }} value={scope.endsAt} onChange={(e) => onChange({ ...scope, endsAt: e.target.value })}/>
          </Stack>
          <Typography variant="caption" color="text.secondary">The schedule temporarily overrides the manual mode and automatically restores it when the window ends.</Typography>
        </>
      )}
    </Stack>
  );
}

export default function SiteAvailability() {
  const [registry, setRegistry] = useState(propertyFallback);
  const [ecosystem, setEcosystem] = useState(blankScope());
  const [sites, setSites] = useState([]);
  const [selectedId, setSelectedId] = useState('clikkle-main');
  const [effective, setEffective] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let live = true;
    getSiteAvailability().then((response) => {
      if (!live) return;
      const nextRegistry = response.registry?.length ? response.registry : propertyFallback;
      setRegistry(nextRegistry);
      setEcosystem({ ...normalizeScope(response.config?.ecosystem), launcherVisible: Boolean(response.config?.ecosystem?.launcherVisible) });
      setSites(nextRegistry.map((property) => {
        const source = response.config?.sites?.find((site) => site.id === property.id) || property;
        return { ...property, ...normalizeScope(source), launcherVisibility: source.launcherVisibility || 'inherit', routeRules: (source.routeRules || []).map(normalizeRoute) };
      }));
      setEffective(response.effective || []);
      setMeta(response.config);
    }).catch((error) => {
      if (live) setMessage({ severity: 'error', text: error.response?.data?.error || 'Unable to load the availability control plane.' });
    }).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, []);

  const selectedSite = useMemo(() => sites.find((site) => site.id === selectedId) || sites[0], [sites, selectedId]);
  const effectiveFor = (domain) => effective.find((state) => state.site === domain)?.mode || 'normal';

  const updateSite = (next) => setSites((current) => current.map((site) => site.id === next.id ? next : site));

  const addRoute = () => {
    if (!selectedSite) return;
    updateSite({
      ...selectedSite,
      routeRules: [...selectedSite.routeRules, { path: '/', manualMode: 'normal', scheduleEnabled: false, scheduledMode: 'maintenance', startsAt: '', endsAt: '' }],
    });
  };

  const updateRoute = (index, patch) => {
    const routeRules = selectedSite.routeRules.map((rule, i) => i === index ? { ...rule, ...patch } : rule);
    updateSite({ ...selectedSite, routeRules });
  };

  const removeRoute = (index) => updateSite({ ...selectedSite, routeRules: selectedSite.routeRules.filter((_, i) => i !== index) });

  const save = async () => {
    setSaving(true); setMessage(null);
    try {
      const payload = {
        ecosystem: serializeScope(ecosystem),
        sites: sites.map((site) => ({
          id: site.id,
          domain: site.domain,
          ...serializeScope(site),
          launcherVisibility: site.launcherVisibility || 'inherit',
          routeRules: site.routeRules.map((rule) => ({ path: rule.path, manualMode: rule.manualMode, ...serializeScope(rule) })),
        })),
      };
      const response = await updateSiteAvailability(payload);
      setEffective(response.effective || []);
      setMeta(response.config);
      setMessage({ severity: 'success', text: 'Availability rules applied.' });
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.error || error.response?.data?.message || 'Update failed.' });
    } finally { setSaving(false); }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1240, mx: 'auto' }}>
      <Stack spacing={1} mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <ShieldCheck size={28} />
          <Typography variant="h4" fontWeight={700}>Clikkle Availability Control Plane</Typography>
          <Chip size="small" color={ecosystem.manualMode === 'normal' ? 'success' : 'warning'} label={`Ecosystem: ${ecosystem.manualMode}`} />
        </Stack>
        <Typography color="text.secondary">Owner-only availability control for the six public Clikkle properties. Ecosystem holds override site holds; site holds override page rules.</Typography>
      </Stack>

      {message && <Alert severity={message.severity} sx={{ mb: 3 }}>{message.text}</Alert>}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={0.5}>Ecosystem override</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>Use only when every participating Clikkle public property must show the same holding experience.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box>
              <Typography fontWeight={700}>Public app launcher</Typography>
              <Typography variant="body2" color="text.secondary">Default visibility for the public ecosystem launcher. Keep hidden until the cinematic replacement is approved.</Typography>
            </Box>
            <FormControlLabel
              control={<Switch checked={Boolean(ecosystem.launcherVisible)} onChange={(e) => setEcosystem((value) => ({ ...value, launcherVisible: e.target.checked }))}/>}
              label={ecosystem.launcherVisible ? 'Visible' : 'Hidden'}
            />
          </Stack>
          <ModeCards value={ecosystem.manualMode} onChange={(manualMode) => setEcosystem((value) => ({ ...value, manualMode }))}/>
          <ScheduleEditor scope={ecosystem} onChange={setEcosystem} label="Schedule ecosystem override" />
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6">Property controls</Typography>
          <Typography variant="body2" color="text.secondary" mb={1.5}>Choose a property, then control the whole site or selected routes.</Typography>
          <Tabs value={selectedId} onChange={(_, value) => setSelectedId(value)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
            {registry.map((property) => <Tab key={property.id} value={property.id} label={property.name} />)}
          </Tabs>

          {selectedSite && (
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography fontWeight={700}>{selectedSite.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedSite.domain}</Typography>
                </Box>
                <Chip color={effectiveFor(selectedSite.domain) === 'normal' ? 'success' : 'warning'} label={`Effective root: ${effectiveFor(selectedSite.domain)}`} />
              </Stack>

              <Box>
                <Typography fontWeight={700} mb={1}>Public app launcher</Typography>
                <Select
                  size="small"
                  value={selectedSite.launcherVisibility || 'inherit'}
                  onChange={(e) => updateSite({ ...selectedSite, launcherVisibility: e.target.value })}
                  sx={{ minWidth: 240 }}
                >
                  <MenuItem value="inherit">Inherit ecosystem default</MenuItem>
                  <MenuItem value="hidden">Hidden on this property</MenuItem>
                  <MenuItem value="visible">Visible on this property</MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>Visibility only. Header geometry and surrounding controls are not repositioned.</Typography>
              </Box>

              <Divider />

              <Box>
                <Typography fontWeight={700} mb={1}>Whole-site mode</Typography>
                <ModeCards value={selectedSite.manualMode} onChange={(manualMode) => updateSite({ ...selectedSite, manualMode })}/>
                <ScheduleEditor scope={selectedSite} onChange={(next) => updateSite({ ...selectedSite, ...next })} label="Schedule whole-site override" />
              </Box>

              <Divider />

              <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} mb={1.5}>
                  <Box>
                    <Typography fontWeight={700}>Page / route overrides</Typography>
                    <Typography variant="body2" color="text.secondary">Applied only when the ecosystem and whole-site modes are Normal. A path also covers its nested routes.</Typography>
                  </Box>
                  <Button startIcon={<Plus size={17}/>} variant="outlined" onClick={addRoute}>Add route</Button>
                </Stack>

                {selectedSite.routeRules.length === 0 ? (
                  <Alert severity="info">No route overrides for this property.</Alert>
                ) : (
                  <Stack spacing={1.5}>
                    {selectedSite.routeRules.map((rule, index) => (
                      <Card key={`${rule._id || 'new'}-${index}`} variant="outlined">
                        <CardContent>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                            <TextField
                              fullWidth size="small" label="Route"
                              placeholder="/new-product"
                              value={rule.path}
                              onChange={(e) => updateRoute(index, { path: e.target.value })}
                            />
                            <Select size="small" sx={{ minWidth: 170 }} value={rule.manualMode} onChange={(e) => updateRoute(index, { manualMode: e.target.value })}>
                              <MenuItem value="normal">Normal</MenuItem>
                              <MenuItem value="maintenance">Maintenance</MenuItem>
                              <MenuItem value="coming-soon">Coming Soon</MenuItem>
                            </Select>
                            <IconButton color="error" aria-label="Remove route override" onClick={() => removeRoute(index)}><Trash2 size={18}/></IconButton>
                          </Stack>
                          <ScheduleEditor scope={rule} onChange={(next) => updateRoute(index, next)} label="Schedule this route" />
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Button size="large" variant="contained" disabled={loading || saving} onClick={save}>{saving ? 'Applying…' : 'Apply availability rules'}</Button>
        <Button size="large" variant="outlined" href="https://clikkle.com/maintenance" target="_blank">Preview maintenance</Button>
        <Button size="large" variant="outlined" href="https://clikkle.com/coming-soon" target="_blank">Preview Coming Soon</Button>
      </Stack>

      {meta?.updatedAt && <Typography variant="caption" color="text.secondary" display="block" mt={2}>Last control-plane update {new Date(meta.updatedAt).toLocaleString()}{meta.updatedBy?.email ? ` by ${meta.updatedBy.email}` : ''}.</Typography>}
    </Box>
  );
}
